import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import RightSidebar from './components/RightSidebar';
import WalletConnectionScreen from './components/WalletConnectionScreen';

import DiagnosticTest from './components/DiagnosticTest';
import ProfileCreationModal from './components/ProfileCreationModal';
import DirectMessageContainer from './components/DirectMessageContainer';
import DAOIntegration from './components/DAOIntegration';
import GovernanceAdmin from './components/GovernanceAdmin';
import ProfilePage from './components/ProfilePage';
import CosmicForgePage from './components/CosmicForgePage';
import ExplorePage from './components/ExplorePage';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import { ServiceProvider, useServices } from './contexts/ServiceContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';
import { secureStorage } from './utils/security';
import config from './config';
import './App.css';

// Main App Content Component (separated to use hooks inside providers)
const AppContent = () => {
  // Wallet connection state
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [forceShowModal, setForceShowModal] = useState(false);
  const [showDirectMessages, setShowDirectMessages] = useState(false);
  const [showDAOGovernance, setShowDAOGovernance] = useState(false);
  const [showGovernanceAdmin, setShowGovernanceAdmin] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCosmicForge, setShowCosmicForge] = useState(false);
  const [showExplore, setShowExplore] = useState(false);
  const { 
    showProfileModal, 
    isCreatingProfile, 
    createProfile, 
    closeProfileModal,
    error: onboardingError,
    retryOnboarding,
    onboardingState,
    manualOnboardingCheck,
    clearAllData,
    isLoading,
    checkUsernameAvailability,
    address: onboardingWalletAddress
  } = useOnboarding();

  // Get wallet state from service context for DAO integration
  const services = useServices();

  // NOTE: We intentionally do NOT auto-sync from OnboardingContext here.
  // The user must explicitly click "Connect Wallet" on the WalletConnectionScreen.
  // OnboardingContext may auto-reconnect silently, but we still require the user
  // to go through the connection screen for a clear UX flow.

  // Handle wallet connection from WalletConnectionScreen
  const handleWalletConnected = async (walletResult) => {
    console.log('🔗 Wallet connected:', walletResult.address);
    setWalletConnected(true);
    setWalletAddress(walletResult.address);
    
    // Check if user has an existing profile
    try {
      if (manualOnboardingCheck) {
        await manualOnboardingCheck(walletResult.address);
        console.log('✅ Onboarding status checked for:', walletResult.address);
      } else {
        console.log('⚠️ manualOnboardingCheck method not available');
      }
    } catch (error) {
      console.error('❌ Error checking onboarding status:', error);
    }
  };
  
  // If wallet is not connected, show the wallet connection screen
  if (!walletConnected || !walletAddress) {
    return (
      <WalletConnectionScreen 
        onWalletConnected={handleWalletConnected}
      />
    );
  }

  const handleWalletDisconnect = () => {
    console.log('🔌 Wallet disconnected');
    setWalletConnected(false);
    setWalletAddress(null);
    // Reset all modals and states
    setShowDirectMessages(false);
    setShowDAOGovernance(false);
    setShowGovernanceAdmin(false);
    setShowProfile(false);
    setShowCosmicForge(false);
  };

  // Handle profile creation
  const handleCreateProfile = async (profileData) => {
    // handleCreateProfile called
    
    try {
      // Calling createProfile
      const result = await createProfile(profileData);
      // createProfile result received
    } catch (error) {
      console.error('❌ Profile creation failed:', error);
      console.error('❌ Error details:', error.stack);
      // Error is handled by the onboarding context
    }
  };

  // Handle opening direct messages
  const handleOpenMessages = () => {
    // Force open DM modal - bypass wallet check for now since wallet is clearly connected
    // The wallet validation will happen inside the DirectMessageContainer
    // DM Button clicked
    
    setShowDirectMessages(true);
    // showDirectMessages state set to: true
    
    // Add a small delay to ensure state update
    setTimeout(() => {
      // Delayed check - showDirectMessages should be true
    }, 100);
  };

  // Handle closing direct messages
  const handleCloseMessages = () => {
    // Closing direct messages
    setShowDirectMessages(false);
  };

  // Handle opening DAO governance
  const handleOpenDAOGovernance = () => {
    const walletConnected = onboardingWalletAddress || 
                           (onboardingState && onboardingState.status === 'completed' && onboardingState.profile);
    
    console.log('🔍 DAO Debug - Wallet sources:', {
      onboardingWalletAddress,
      onboardingState,
      walletConnected
    });
    
    if (walletConnected) {
      // Opening DAO Governance Admin Panel
      setShowGovernanceAdmin(true);
    } else {
      // DAO: Wallet not connected
      alert('Please connect your wallet to access DAO Governance features');
    }
  };

  // Handle closing DAO governance
  const handleCloseDAOGovernance = () => {
    // Closing DAO Governance
    setShowDAOGovernance(false);
    setShowGovernanceAdmin(false);
  };

  // Handle opening profile page
  const handleOpenProfile = () => {
    // DAO Debug - checking wallet sources
    console.log('DAO Debug - checking wallet sources');
    // Check if we have any wallet indication
    const hasWalletIndication = onboardingWalletAddress || onboardingState?.profile;
    
    console.log('🔍 Profile Debug - hasWalletIndication:', hasWalletIndication);
    console.log('DAO Debug - checking wallet sources');
    console.log('🔍 Profile Debug - Final check:', {
      onboardingWalletAddress,
      hasProfile: !!onboardingState?.profile
    });
    
    if (hasWalletIndication) {
      setShowProfile(true);
      const walletAddress = onboardingWalletAddress || 'connected';
      console.log('👤 Opening Profile for wallet:', walletAddress);
    } else {
      console.log('❌ Cannot open Profile - no wallet connected');
    }
  };

  // Handle closing profile page
  const handleCloseProfile = () => {
    console.log('👤 Closing profile page...');
    setShowProfile(false);
  };

  // Handle opening Cosmic Forge page
  const handleOpenCosmicForge = () => {
    // Check multiple sources for wallet connection
    const phantomConnected = window.solana?.isConnected && window.solana?.publicKey;
    const onboardingConnected = !!onboardingWalletAddress;
    const serviceConnected = !!(services?.solanaService?.wallet?.publicKey);
    
    const walletConnected = phantomConnected || onboardingConnected || serviceConnected;
    
    console.log('🌌 Cosmic Forge wallet check:', {
      phantomConnected: !!phantomConnected,
      onboardingConnected,
      serviceConnected,
      finalResult: walletConnected,
      onboardingWalletAddress,
      phantomAddress: window.solana?.publicKey?.toString()
    });
    
    if (walletConnected) {
      console.log('🌌 Opening Cosmic Avatar Forge...');
      setShowCosmicForge(true);
    } else {
      console.log('❌ Cosmic Forge: Wallet not connected');
      alert('Please connect your wallet to access the Cosmic Avatar Forge');
    }
  };

  // Handle closing Cosmic Forge page
  const handleCloseCosmicForge = () => {
    console.log('🌌 Closing Cosmic Avatar Forge...');
    setShowCosmicForge(false);
  };

  // Show loading screen during restoration
  if (isLoading) {
    return (
      <div className="App">
        <LoadingScreen message="Restoring your cosmic connection..." />
      </div>
    );
  }

  return (
    <div className="App">


      {/* Diagnostic Test Toggle */}
      <div style={{ 
        position: 'fixed', 
        top: '10px', 
        left: '10px', 
        zIndex: 999,
        backgroundColor: '#2196F3',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
        fontSize: '12px'
      }} onClick={() => setShowDiagnostics(!showDiagnostics)}>
        {showDiagnostics ? '❌' : '🔧'}
      </div>

      {showDiagnostics ? (
        <DiagnosticTest />
      ) : showExplore ? (
        /* Explore Full Page */
        <ExplorePage
          onClose={() => setShowExplore(false)}
        />
      ) : showCosmicForge ? (
        /* Cosmic Forge Full Page */
        <CosmicForgePage
          onClose={handleCloseCosmicForge}
        />
      ) : showProfile ? (
        /* Profile Full Page */
        <ProfilePage
          onClose={handleCloseProfile}
        />
      ) : (
        <>
          {/* Left Sidebar Navigation */}
          <Sidebar 
            walletAddress={onboardingWalletAddress}
            onOpenMessages={handleOpenMessages}
            onOpenDAOGovernance={handleOpenDAOGovernance}
            onOpenProfile={handleOpenProfile}
            onOpenCosmicForge={handleOpenCosmicForge}
            onOpenExplore={() => setShowExplore(true)}
          />
          
          {/* Main Content Area */}
          <MainContent walletAddress={walletAddress} />
          
          {/* Right Sidebar */}
          <RightSidebar walletAddress={onboardingWalletAddress} />
        </>
      )}

      {/* Profile Creation Modal */}
      <ProfileCreationModal
        isOpen={showProfileModal || forceShowModal}
        onClose={() => {
          closeProfileModal();
          setForceShowModal(false);
        }}
        onCreateProfile={handleCreateProfile}
        walletAddress={onboardingWalletAddress}
        isCreating={isCreatingProfile}
        checkUsernameAvailability={checkUsernameAvailability}
      />

      {/* Direct Messages Container */}
      <DirectMessageContainer
        isOpen={showDirectMessages}
        onClose={handleCloseMessages}
      />

      {/* DAO Governance Integration */}
      {showDAOGovernance && (
        <DAOIntegration
          onClose={handleCloseDAOGovernance}
        />
      )}

      {/* Governance Admin Panel */}
      {showGovernanceAdmin && (
        <GovernanceAdmin
          isOpen={showGovernanceAdmin}
          onClose={handleCloseDAOGovernance}
        />
      )}






      {/* Show onboarding errors */}
      {onboardingError && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: '#ff4444',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          zIndex: 1001,
          maxWidth: '300px',
          fontSize: '14px'
        }}>
          ❌ {onboardingError}
        </div>
      )}
    </div>
  );
};

// Main App Component with Providers and Security
function App() {
  return (
    <ErrorBoundary
      onError={(error, errorInfo, errorId) => {
        console.error('🚨 App-level error caught:', errorId);
      }}
      showDetails={process.env.NODE_ENV === 'development'}
    >
      <ServiceProvider>
        <OnboardingProvider>
          <AppContent />
        </OnboardingProvider>
      </ServiceProvider>
    </ErrorBoundary>
  );
}

export default App;
